/**
 * Deterministic trade-show strategy rules engine.
 *
 * Pure function: given the user's questionnaire answers, returns a prioritized
 * list of recommendations derived from Nicole's strategy framework.
 *
 * This module is intentionally standalone — it knows nothing about the AI /
 * LightRAG layer. The AI-polish layer (added later) should CONSUME this
 * module's output, not replace its logic. To demote or remove the rules
 * later, stop calling generateStrategyRecommendations() and let the AI layer
 * run on its own — no changes needed inside this file.
 *
 * Answer key convention: `${category}-${questionIndex}`.
 * Single-select answers are strings; multi-select answers are arrays.
 */

// Question index maps per category (must match strategyQuestions in PlayBook.js)
const Q = {
  'Lead Generation': {
    exhibited: 0, brandKnown: 1, attendeeList: 2, ownList: 3,
    speaking: 4, giveaway: 5, budget: 6, booth: 7,
  },
  'Brand Awareness': {
    customerPct: 0, brandKnown: 1, sponsorship: 2, announcement: 3,
    sponsorBudget: 4, booth: 5,
  },
  'Customer Engagement': {
    customerPct: 0, customerList: 1, attendeeList: 2, engageMethods: 3,
    connectionEvent: 4, audience: 5,
  },
};

function get(answers, category, key) {
  const idx = Q[category] && Q[category][key];
  if (idx === undefined) return undefined;
  return answers[`${category}-${idx}`];
}

function has(val) {
  if (val === undefined || val === null) return false;
  if (Array.isArray(val)) return val.length > 0;
  return String(val).trim().length > 0;
}

/**
 * Returns recommendations for a category, or [] if that category is untouched.
 * Each recommendation: { priority, title, reason }.
 */
function leadGenRules(a) {
  const c = 'Lead Generation';
  // Only run if the user engaged with this goal at all
  const engaged = Object.keys(Q[c]).some(k => has(get(a, c, k)));
  if (!engaged) return [];

  const recs = [];
  const exhibited = get(a, c, 'exhibited');
  const attendeeList = get(a, c, 'attendeeList');
  const ownList = get(a, c, 'ownList');
  const speaking = get(a, c, 'speaking');

  // Pre-show marketing is always recommended for lead gen
  recs.push({ priority: 1, title: 'Run pre-show email and social marketing',
    reason: 'Pre-show outreach is foundational for lead generation regardless of history.' });

  if (exhibited === 'Never') {
    recs.push({ priority: 1, title: 'Send a press release announcing your attendance',
      reason: 'This is a new show for you — a press release builds awareness that you\'ll be there.' });
  }

  if (attendeeList === 'Yes' || ownList === 'Yes') {
    recs.push({ priority: 1, title: 'Schedule pre-show emails sharing your booth number and key giveaways',
      reason: 'You have a contact or attendee list — reach them directly before the show.' });
  } else if (attendeeList === 'No' && ownList === 'No') {
    recs.push({ priority: 2, title: 'Build a top-prospect list to target',
      reason: 'No list is available yet — identify and prioritize key prospects to reach out to.' });
  }

  if (speaking === 'Yes') {
    recs.push({ priority: 1, title: 'Put a QR code or lead scanner in the speaking room and hand out a flyer/offer',
      reason: 'You have a speaker — capture contacts from the audience and drive them to your booth.' });
  } else if (speaking === 'No') {
    recs.push({ priority: 2, title: 'Pursue a speaking session',
      reason: 'No one is speaking yet — a session is a strong lead and traffic driver.' });
  }

  recs.push({ priority: 3, title: 'Run in-booth educational presentations',
    reason: 'Well-marketed in-booth sessions drive traffic and leads.' });

  return recs;
}

function brandAwarenessRules(a) {
  const c = 'Brand Awareness';
  const engaged = Object.keys(Q[c]).some(k => has(get(a, c, k)));
  if (!engaged) return [];

  const recs = [];
  const customerPct = get(a, c, 'customerPct');
  const announcement = get(a, c, 'announcement');
  const sponsorBudget = get(a, c, 'sponsorBudget');

  if (customerPct === '30-60%' || customerPct === '60%+') {
    recs.push({ priority: 1, title: 'Focus on high-value customer engagement (tiered gifts, scheduled meetings)',
      reason: 'A high share of attendees are already customers — ensure the right conversations happen.' });
  } else if (customerPct === 'Less than 10%' || customerPct === '10-30%') {
    recs.push({ priority: 1, title: 'Lean toward lead generation and relationship-building outreach',
      reason: 'Most attendees are not yet customers — prioritize reaching new prospects.' });
  }

  if (announcement === 'Yes') {
    recs.push({ priority: 1, title: 'Push your announcement out before the show and use it to drive booth traffic',
      reason: 'You have newsworthy news — publicize it ahead of time; consider media contact for bigger news.' });
  }

  if (sponsorBudget === 'Yes') {
    recs.push({ priority: 2, title: 'Add a sponsorship to build awareness',
      reason: 'You have sponsorship budget available — sponsorships increase visibility at the show.' });
  }

  return recs;
}

function customerEngagementRules(a) {
  const c = 'Customer Engagement';
  const engaged = Object.keys(Q[c]).some(k => has(get(a, c, k)));
  if (!engaged) return [];

  const recs = [];
  const customerList = get(a, c, 'customerList');
  const attendeeList = get(a, c, 'attendeeList');
  const connectionEvent = get(a, c, 'connectionEvent');

  if (customerList === 'Yes') {
    recs.push({ priority: 1, title: 'Begin pre-show outreach to your identified customers',
      reason: 'You have a customer list — start scheduling conversations before the show.' });
  } else {
    recs.push({ priority: 2, title: 'Gather a list of customers who normally attend this show',
      reason: 'Pull old leads and known customers so you can plan outreach.' });
  }

  if (attendeeList === 'Yes') {
    recs.push({ priority: 1, title: 'Cross-reference the attendee list to find your customers',
      reason: 'The attendee list is available — match it against your customer base to target the right people.' });
  }

  if (connectionEvent === 'Yes') {
    recs.push({ priority: 1, title: 'Host a connection event (dinner, social, or hospitality)',
      reason: 'You\'re open to it — small-group events let customers and prospects mingle.' });
  }

  recs.push({ priority: 3, title: 'Pair a subject-matter expert with a sales rep in every customer conversation, and log who met whom',
    reason: 'Ensures quality conversations and clean follow-up tracking.' });

  return recs;
}

/**
 * Main entry point. Returns { recommendations: [...] } sorted by priority.
 */
export function generateStrategyRecommendations(answers) {
  const all = [
    ...leadGenRules(answers),
    ...brandAwarenessRules(answers),
    ...customerEngagementRules(answers),
  ];
  all.sort((x, y) => x.priority - y.priority);
  return { recommendations: all };
}
